import { AxiosCall, AxiosPatch, AxiosPost, AxiosDelete } from '../../../utils/axios';

const buildQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();

  // Batch filter (for task alerts related to specific batch)
  if (params.batch) {
    searchParams.append('related_batch', params.batch); // Use related_batch field
  }

  // Status filter
  if (params.status && params.status !== 'all') {
    searchParams.append('status', params.status);
  }

  // Alert type filter (e.g., 'task' for task alerts)
  if (params.alert_type) {
    searchParams.append('alert_type', params.alert_type);
  }

  // Priority filter
  if (params.priority) {
    searchParams.append('priority', params.priority);
  }

  // Growth stage filter
  if (params.growth_stage) {
    searchParams.append('growth_stage', params.growth_stage);
  }

  // Organization filter
  if (params.organization) {
    searchParams.append('organization', params.organization);
  }

  // Active only filter (only show active alerts)
  if (params.active_only !== undefined) {
    searchParams.append('status', 'active'); // Map to status=active
  }

  // Overdue only filter
  if (params.overdue_only) {
    searchParams.append('overdue_only', params.overdue_only);
  }

  // Upcoming days filter
  if (params.upcoming_days) {
    searchParams.append('upcoming_days', params.upcoming_days);
  }

  // Search filter
  if (params.search) {
    searchParams.append('search', params.search);
  }

  // Show scheduled alerts
  if (params.show_scheduled !== undefined) {
    searchParams.append('show_scheduled', params.show_scheduled);
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

// ==================== TASK ALERTS ====================
// NOTE: Task alerts now use the unified /mycore/alerts/ endpoint
// They are identified by having a template field and alert_type='task'

export const fetchTaskAlerts = (params) => {
  // Add filter to get only task alerts (those with templates)
  const taskParams = {
    ...params,
    alert_type: 'task', // Filter for task alerts
  };
  return AxiosCall(`/mycore/alerts/${buildQueryString(taskParams)}`);
};

export const fetchTaskAlertDetail = (alertId) =>
  AxiosCall(`/mycore/alerts/${alertId}/`);

export const createTaskAlert = (payload) =>
  AxiosPost('/mycore/alerts/', { ...payload, alert_type: 'task' });

export const updateTaskAlert = (alertId, payload) =>
  AxiosPatch(`/mycore/alerts/${alertId}/`, payload);

export const deleteTaskAlert = (alertId) =>
  AxiosDelete(`/mycore/alerts/${alertId}/`);

export const completeTaskAlert = (alertId, notes = '') =>
  AxiosPatch(`/mycore/alerts/${alertId}/complete/`, { completion_notes: notes });

export const dismissTaskAlert = (alertId) =>
  AxiosPatch(`/mycore/alerts/${alertId}/dismiss/`, {});

export const reopenTaskAlert = (alertId) =>
  AxiosPatch(`/mycore/alerts/${alertId}/reopen/`, {});

export const fetchTaskAlertSummary = (params = {}) => {
  const queryString = params.organization ? `?organization=${params.organization}` : '';
  return AxiosCall(`/mycore/alerts/summary/${queryString}`);
};

export const fetchUpcomingTaskAlerts = (days = 7, organizationId = null) => {
  const params = { days };
  if (organizationId) {
    params.organization = organizationId;
  }
  const searchParams = new URLSearchParams(params);
  return AxiosCall(`/mycore/alerts/upcoming/?${searchParams.toString()}`);
};

// Note: Overdue status is now handled automatically by backend signals
// This endpoint is kept for backward compatibility but may not be needed
export const updateOverdueTaskAlerts = () =>
  AxiosPost('/mycore/alerts/update-overdue/', {});

// ==================== ALERT TEMPLATES ====================

export const fetchAlertTemplates = (params) => {
  const searchParams = new URLSearchParams();

  if (params?.recipe) {
    searchParams.append('recipe', params.recipe);
  }

  if (params?.general_only) {
    searchParams.append('general_only', params.general_only);
  }

  if (params?.is_active !== undefined) {
    searchParams.append('is_active', params.is_active);
  }

  const queryString = searchParams.toString();
  return AxiosCall(`/mycore/alert-templates/${queryString ? `?${queryString}` : ''}`);
};

export const fetchAlertTemplateDetail = (templateId) =>
  AxiosCall(`/mycore/alert-templates/${templateId}/`);

export const createAlertTemplate = (payload) =>
  AxiosPost('/mycore/alert-templates/', payload);

export const updateAlertTemplate = (templateId, payload) =>
  AxiosPatch(`/mycore/alert-templates/${templateId}/`, payload);

export const deleteAlertTemplate = (templateId) =>
  AxiosDelete(`/mycore/alert-templates/${templateId}/`);

export const fetchGeneralTemplates = () =>
  AxiosCall('/mycore/alert-templates/general/');

export const bulkCreateTemplates = (templates) =>
  AxiosPost('/mycore/alert-templates/bulk-create/', { templates });

export const fetchSpeciesTemplates = (speciesId) =>
  AxiosCall(`/mycore/alert-templates/species/${speciesId}/`);

export const fetchSystemTemplates = () =>
  AxiosCall('/mycore/alert-templates/system/');

export const cloneAlertTemplate = (templateId, payload) =>
  AxiosPost(`/mycore/alert-templates/${templateId}/clone/`, payload);

export default {
  // Task Alerts
  fetchTaskAlerts,
  fetchTaskAlertDetail,
  createTaskAlert,
  updateTaskAlert,
  deleteTaskAlert,
  completeTaskAlert,
  dismissTaskAlert,
  reopenTaskAlert,
  fetchTaskAlertSummary,
  fetchUpcomingTaskAlerts,
  updateOverdueTaskAlerts,

  // Alert Templates
  fetchAlertTemplates,
  fetchAlertTemplateDetail,
  createAlertTemplate,
  updateAlertTemplate,
  deleteAlertTemplate,
  fetchGeneralTemplates,
  bulkCreateTemplates,
  fetchSpeciesTemplates,
  fetchSystemTemplates,
  cloneAlertTemplate,
};
